/**
 * Browser Language Detection Utility
 * 
 * Automatically detects user's country/region from multiple sources and maps to appropriate language.
 * Checks against 14 supported languages in i18n, defaults to English if language not available.
 */

import { LanguageCode, SUPPORTED_LANGUAGES } from "./languageRouting";

// Cache for IP geolocation to avoid multiple API calls
let cachedCountryCode: string | null = null;
let geolocationAttempted = false;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Clear stale cache on module load
const clearStaleCache = () => {
  if (cacheTimestamp && Date.now() - cacheTimestamp > CACHE_DURATION) {
    cachedCountryCode = null;
    geolocationAttempted = false;
    cacheTimestamp = null;
  }
};

clearStaleCache();

// Map of country codes to language codes
const REGION_TO_LANGUAGE_MAP: Record<string, LanguageCode> = {
  // English-speaking countries
  US: "en",
  GB: "en",
  CA: "en",
  AU: "en",
  NZ: "en",
  IE: "en",
  ZA: "en",
  SG: "en",
  IN: "hi", // India - Hindi (but will also support English)
  
  // Italian
  IT: "it",
  SM: "it", // San Marino
  VA: "it", // Vatican
  
  // Spanish
  ES: "es",
  MX: "es",
  AR: "es",
  CO: "es",
  CL: "es",
  PE: "es",
  VE: "es",
  EC: "es",
  GT: "es",
  CU: "es",
  BO: "es",
  DO: "es",
  HN: "es",
  PY: "es",
  SV: "es",
  NI: "es",
  CR: "es",
  PA: "es",
  UY: "es",
  
  // French
  FR: "fr",
  BE: "fr",
  CH: "fr",
  LU: "fr",
  MC: "fr",
  CA_QC: "fr", // Quebec
  
  // German
  DE: "de",
  AT: "de",
  CH_DE: "de", // Switzerland (German)
  LI: "de", // Liechtenstein
  
  // Portuguese
  PT: "pt",
  BR: "pt",
  AO: "pt",
  MZ: "pt",
  
  // Russian
  RU: "ru",
  BY: "ru",
  KZ: "ru",
  KG: "ru",
  
  // Chinese
  CN: "zh",
  TW: "zh",
  HK: "zh",
  MO: "zh",
  
  // Japanese
  JP: "ja",
  
  // Arabic
  SA: "ar",
  AE: "ar",
  EG: "ar",
  IQ: "ar",
  JO: "ar",
  KW: "ar",
  LB: "ar",
  LY: "ar",
  MA: "ar",
  OM: "ar",
  QA: "ar",
  SY: "ar",
  TN: "ar",
  YE: "ar",
  
  // Korean
  KR: "ko",
  
  // Dutch
  NL: "nl",
  BE_NL: "nl", // Belgium (Flemish)
  
  // Czech
  CZ: "cs",
  SK: "cs", // Slovakia (similar languages)
};

/**
 * Detect country from IP geolocation API
 * NOTE: Direct IP geolocation from browser is disabled due to CORS restrictions.
 * This function now returns null and relies on browser-based detection methods
 * (browser locale, timezone) which work without external API calls.
 * For accurate IP-based detection, use the backend API endpoint: /api/v1/auth/detect-location
 */
const detectCountryFromIP = async (): Promise<string | null> => {
  // Return cached result if available
  if (cachedCountryCode) {
    return cachedCountryCode;
  }
  
  // Don't attempt multiple times if it already failed
  if (geolocationAttempted) {
    return null;
  }
  
  geolocationAttempted = true;
  
  // Direct IP geolocation from browser is disabled due to CORS restrictions.
  // The ipapi.co API doesn't allow cross-origin requests from browsers.
  // Instead, this function will return null and the caller will fall back to
  // browser-based detection methods (locale, timezone) which work reliably.
  // For accurate IP-based detection, use the backend API endpoint instead.
  
  return null;
};

/**
 * Get user's country code from browser's locale
 */
const getCountryFromBrowserLocale = (): string | null => {
  try {
    // Get the full locale (e.g., "en-US", "it-IT", "es-MX")
    const locale = navigator.language || (navigator as any).userLanguage;
    
    if (!locale) return null;
    
    // Extract country code from locale
    const parts = locale.split("-");
    if (parts.length === 2) {
      return parts[1].toUpperCase();
    }
    
    // If only language code is available (e.g., "en"), try to infer
    const langCode = parts[0].toLowerCase();
    
    // Map language codes to likely countries
    const langToCountryMap: Record<string, string> = {
      en: "US",
      it: "IT",
      es: "ES",
      fr: "FR",
      de: "DE",
      pt: "PT",
      ru: "RU",
      zh: "CN",
      ja: "JP",
      ar: "SA",
      hi: "IN",
      ko: "KR",
      nl: "NL",
      cs: "CZ",
    };
    
    return langToCountryMap[langCode] || null;
  } catch (error) {
    console.error("Error detecting country from browser locale:", error);
    return null;
  }
};

/**
 * Get user's timezone and infer region
 */
const getCountryFromTimezone = (): string | null => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map common timezones to country codes
    const timezoneMap: Record<string, string> = {
      "America/New_York": "US",
      "America/Chicago": "US",
      "America/Denver": "US",
      "America/Los_Angeles": "US",
      "America/Mexico_City": "MX",
      "America/Sao_Paulo": "BR",
      "America/Buenos_Aires": "AR",
      "Europe/London": "GB",
      "Europe/Paris": "FR",
      "Europe/Berlin": "DE",
      "Europe/Rome": "IT",
      "Europe/Madrid": "ES",
      "Europe/Amsterdam": "NL",
      "Europe/Prague": "CZ",
      "Europe/Moscow": "RU",
      "Asia/Tokyo": "JP",
      "Asia/Shanghai": "CN",
      "Asia/Seoul": "KR",
      "Asia/Dubai": "AE",
      "Asia/Kolkata": "IN",
      "Australia/Sydney": "AU",
    };
    
    return timezoneMap[timezone] || null;
  } catch (error) {
    console.error("Error detecting country from timezone:", error);
    return null;
  }
};

/**
 * Detect user's region/country code synchronously
 * For async IP detection, use detectUserRegionAsync()
 */
export const detectUserRegion = (): string | null => {
  // Return cached IP result if available
  if (cachedCountryCode) return cachedCountryCode;
  
  // Try multiple methods
  const countryFromLocale = getCountryFromBrowserLocale();
  if (countryFromLocale) return countryFromLocale;
  
  const countryFromTimezone = getCountryFromTimezone();
  if (countryFromTimezone) return countryFromTimezone;
  
  return null;
};

/**
 * Detect user's region/country code asynchronously using IP geolocation
 * This is more accurate as it detects the actual location (including VPN)
 */
export const detectUserRegionAsync = async (): Promise<string | null> => {
  // Priority 1: Try IP-based detection (most accurate)
  const countryFromIP = await detectCountryFromIP();
  if (countryFromIP) return countryFromIP;
  
  // Priority 2: Browser locale
  const countryFromLocale = getCountryFromBrowserLocale();
  if (countryFromLocale) return countryFromLocale;
  
  // Priority 3: Timezone
  const countryFromTimezone = getCountryFromTimezone();
  if (countryFromTimezone) return countryFromTimezone;
  
  return null;
};

/**
 * Get language code based on user's country/region (synchronous)
 * Maps country code to appropriate language using REGION_TO_LANGUAGE_MAP
 * Returns the language code if available, otherwise returns 'en'
 */
export const getLanguageFromRegion = (): LanguageCode => {
  // Check if we have cached IP-based country
  if (cachedCountryCode) {
    const mappedLanguage = REGION_TO_LANGUAGE_MAP[cachedCountryCode];
    if (mappedLanguage) {
      return mappedLanguage;
    }
  }
  
  // Priority 1: Try to extract country from browser locale
  try {
    const browserLanguage = navigator.language || (navigator as any).userLanguage;
    if (browserLanguage) {
      const parts = browserLanguage.split("-");
      
      // If we have a country code (e.g., "nl-NL", "en-US")
      if (parts.length === 2) {
        const countryCode = parts[1].toUpperCase();
        const mappedLanguage = REGION_TO_LANGUAGE_MAP[countryCode];
        
        if (mappedLanguage) {
          return mappedLanguage;
        }
      }
      
      // Fallback: Try language code directly
      const langCode = parts[0].toLowerCase();
      if (SUPPORTED_LANGUAGES.includes(langCode as LanguageCode)) {
        return langCode as LanguageCode;
      }
    }
  } catch (error) {
    // Silent fallback
  }
  
  // Priority 2: Try timezone-based region detection
  const region = detectUserRegion();
  
  if (region) {
    const language = REGION_TO_LANGUAGE_MAP[region];
    if (language) {
      return language;
    }
  }
  
  // Priority 3: Default to English
  return "en";
};

/**
 * Get language code based on user's country/region (asynchronous with IP detection)
 * This is more accurate as it uses IP geolocation API
 */
export const getLanguageFromRegionAsync = async (): Promise<LanguageCode> => {
  const region = await detectUserRegionAsync();
  
  if (region) {
    const language = REGION_TO_LANGUAGE_MAP[region];
    if (language) {
      return language;
    }
  }
  
  return getLanguageFromRegion();
};

/**
 * Check if a language code is supported in i18n (language selector)
 */
export const isLanguageSupported = (languageCode: string): boolean => {
  return SUPPORTED_LANGUAGES.includes(languageCode as LanguageCode);
};

/**
 * Check if a language is available in the backend database
 */
export const isLanguageAvailable = (
  languageCode: string,
  activeLanguages: Array<{ initial?: string; code?: string; languageCode?: string; name?: string }>
): boolean => {
  if (!activeLanguages || activeLanguages.length === 0) {
    return false;
  }
  
  return activeLanguages.some(lang => {
    const code = lang.initial || lang.code || lang.languageCode || "";
    return code.toLowerCase() === languageCode.toLowerCase() ||
           lang.name?.toLowerCase() === languageCode.toLowerCase();
  });
};

/**
 * Get the best language for the user based on their region and available languages
 * Priority: Saved preference > Region detection > English fallback
 */
export const getBestLanguageForUser = (
  activeLanguages: Array<{ 
    id: string; 
    initial?: string;
    code?: string; 
    languageCode?: string; 
    name?: string;
    isDefault?: boolean;
  }>,
  savedLanguageId?: string | null
): LanguageCode => {
  // Priority 1: Return saved preference if valid
  if (savedLanguageId && savedLanguageId !== "null" && savedLanguageId !== "undefined") {
    const savedLang = activeLanguages.find((lang) => 
      lang.id === savedLanguageId || 
      String(lang.id) === String(savedLanguageId) ||
      String(lang.id).trim() === String(savedLanguageId).trim()
    );
    
    if (savedLang) {
      const langCode = (savedLang.initial || savedLang.code || savedLang.languageCode || "").toLowerCase();
      if (SUPPORTED_LANGUAGES.includes(langCode as LanguageCode)) {
        return langCode as LanguageCode;
      }
    }
  }
  
  // Priority 2: Try to detect language from region
  const regionLanguage = getLanguageFromRegion();
  
  if (isLanguageAvailable(regionLanguage, activeLanguages)) {
    return regionLanguage;
  }
  
  // Priority 3: Default to English if available
  if (isLanguageAvailable("en", activeLanguages)) {
    return "en";
  }
  
  // Priority 4: Use backend default
  const defaultLang = activeLanguages.find(lang => lang.isDefault);
  if (defaultLang) {
    const langCode = (defaultLang.initial || defaultLang.code || defaultLang.languageCode || "en").toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(langCode as LanguageCode)) {
      return langCode as LanguageCode;
    }
  }
  
  // Final fallback
  return "en";
};

/**
 * Get language object from active languages by language code
 */
export const getLanguageByCode = <T extends { 
  id: string; 
  initial?: string;
  code?: string; 
  languageCode?: string; 
  name?: string;
}>(
  languageCode: LanguageCode,
  activeLanguages: T[]
): T | null => {
  return activeLanguages.find(lang => {
    const code = (lang.initial || lang.code || lang.languageCode || "").toLowerCase();
    return code === languageCode.toLowerCase();
  }) || null;
};

